import React, { useEffect, useState } from 'react';
import { useUIStore } from '../../store/uiStore';

// Dashboard-specific dark mode toggle that persists to localStorage
// and overrides global dark mode while on dashboard routes.
const STORAGE_KEY = 'fex-dashboard-dark';

const DashboardDarkModeToggle = ({ className = '', size = 'md' }) => {
  const [isDark, setIsDark] = useState(false);
  const { initDarkMode, setDarkMode } = useUIStore();

  useEffect(() => {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) {
      // No dashboard-specific preference; reflect current global state
      initDarkMode();
      setIsDark(document.documentElement.classList.contains('dark'));
    } else {
      const pref = raw === 'true';
      setIsDark(pref);
      // Apply preference immediately when component mounts
      if (pref) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
    }
    // On unmount, restore global setting from uiStore
    return () => {
      initDarkMode();
    };
  }, [initDarkMode]);

  const toggle = () => {
    const next = !isDark;
    setIsDark(next);
    localStorage.setItem(STORAGE_KEY, String(next));
    // Sync to global UI store so dark mode affects all pages
    setDarkMode(next);
  };

  const sizeClasses = {
    sm: 'p-1.5 w-8 h-8',
    md: 'p-2 w-10 h-10',
    lg: 'p-3 w-12 h-12'
  };

  const iconSizes = {
    sm: 'w-4 h-4',
    md: 'w-5 h-5',
    lg: 'w-6 h-6'
  };

  return (
    <button
      onClick={toggle}
      className={`flex items-center gap-2 rounded-lg transition-all duration-200 
        ${isDark 
          ? 'bg-gray-700 text-yellow-400 hover:bg-gray-600' 
          : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
        } ${sizeClasses[size]} ${className}`}
      aria-label="Toggle dashboard dark mode"
      title={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
    >
      {isDark ? (
        // Sun icon (Light mode)
        <svg className={iconSizes[size]} fill="currentColor" viewBox="0 0 20 20">
          <path 
            fillRule="evenodd" 
            d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" 
            clipRule="evenodd" 
          />
        </svg>
      ) : (
        // Moon icon (Dark mode)
        <svg className={iconSizes[size]} fill="currentColor" viewBox="0 0 20 20">
          <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
        </svg>
      )}
    </button>
  );
};

export default DashboardDarkModeToggle;